/**
 * Bulk Lead Import — staged workspace (Upload → Map → Review → Confirm → Result).
 *
 * This screen NEVER creates a canonical record until the final confirmation:
 * upload, mapping, cell edits and preview all run against the staging session
 * (`/api/clinic/leads/imports/...`). The single-patient Add Patient flow and
 * its modal are untouched; this is a separate, additional action.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, Upload, FileSpreadsheet, Download, AlertTriangle, CheckCircle2,
  Loader2, Users, RotateCcw, Filter, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { api } from '../../../utils/api';
import { toast } from 'sonner';

const PAGE_SIZE = 25;
const FILTERS = ['all', 'selected', 'invalid', 'duplicate', 'excluded'];
const EDITABLE = [
  'patient_name', 'patient_email', 'patient_phone', 'patient_country',
  'treatment_interest', 'source_channel', 'priority', 'budget_range', 'notes', 'tags',
];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
// A repeat of the SAME treatment can never be imported, so it is not offered
// as a restorable row — only a genuine second enquiry is.
const BLOCKING_DUPLICATES = ['in_file', 'existing_lead'];

// An invalid row must be impossible to miss. It was previously auto-excluded
// first, so the grey `opacity-60` of an excluded row won the cascade and the
// red tint never showed — the row looked merely disabled instead of wrong.
// Invalid now always wins, keeps full-contrast text, and carries a left bar
// and an icon so the state reads without relying on colour at all.
const ROW_TINT = (row) => {
  if (!row.valid) return 'bg-rose-100/70 border-l-4 border-l-rose-600 text-rose-950';
  if (row.excluded) return 'bg-slate-50 opacity-60';
  return '';
};
const SOURCE_CHANNELS = [
  'SELF_SOURCED', 'SOCIAL_MEDIA', 'REFERRAL', 'WALK_IN', 'PHONE_INQUIRY',
  'WEBSITE', 'EVENT', 'PARTNER', 'OTHER',
];

// The API error handler flattens a dict `detail` to the top level
// ({error: true, code, message}), so the code — not `message` — carries the
// reason. Translate it; fall back to the generic per-action message.
const useImportError = (t) => (err, fallbackKey) => {
  const data = err?.response?.data || {};
  const detail = (data.detail && typeof data.detail === 'object') ? data.detail : data;
  const code = detail?.code;
  if (code) {
    return t(`provider.leads.bulkImport.errors.${code}`, {
      defaultValue: t(`provider.leads.bulkImport.errors.${fallbackKey}`),
    });
  }
  return t(`provider.leads.bulkImport.errors.${fallbackKey}`);
};

const downloadBlob = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const SummaryStrip = ({ summary, t }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="import-summary">
    {[
      ['total_rows', 'total', 'text-slate-700'],
      ['valid_rows', 'valid', 'text-teal-700'],
      ['invalid_rows', 'invalid', 'text-rose-700'],
      ['duplicate_rows', 'duplicates', 'text-amber-700'],
      ['excluded_rows', 'excluded', 'text-slate-500'],
      ['selected_rows', 'selected', 'text-sky-700'],
    ].map(([key, label, tone]) => (
      <div key={key} className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`summary-${label}`}>
        <div className={`text-lg font-semibold ${tone}`}>{summary?.[key] ?? 0}</div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500">
          {t(`provider.leads.bulkImport.summary.${label}`)}
        </div>
      </div>
    ))}
  </div>
);

// The messages themselves are the primary signal: they are announced to screen
// readers and stay readable on the red row tint.
const RowErrors = ({ errors, t }) => (
  <div className="flex flex-wrap gap-1" role="alert">
    {errors.map((e, i) => (
      <span
        key={i}
        className="text-[10px] font-medium text-rose-900 bg-white border border-rose-400 rounded px-1 py-0.5"
      >
        {t(`provider.leads.bulkImport.errors.${e.code}`, { defaultValue: e.code })}
      </span>
    ))}
  </div>
);

export const BulkLeadImport = ({ isOpen, onClose, onImported, reps = [] }) => {
  const { t } = useTranslation();
  const importError = useImportError(t);
  const fileRef = useRef(null);
  const [stage, setStage] = useState('upload');
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [filteredCount, setFilteredCount] = useState(0);
  const [canAssignOthers, setCanAssignOthers] = useState(false);
  const [result, setResult] = useState(null);
  const [catalog, setCatalog] = useState({ treatments: [], countries: [] });

  useEffect(() => {
    if (!isOpen) return;
    setStage('upload'); setSession(null); setRows([]); setResult(null);
    setFilter('all'); setPage(1);
    Promise.all([
      api.get('/public/catalog/treatments').catch(() => ({ data: {} })),
      api.get('/public/catalog/countries').catch(() => ({ data: {} })),
    ]).then(([tr, co]) => {
      const treatments = (tr?.data?.treatments || [])
        .map((x) => (typeof x.name === 'string' ? x.name : (x.name?.en || x.slug)))
        .filter(Boolean).sort((a, b) => a.localeCompare(b));
      setCatalog({
        treatments,
        countries: (co?.data?.countries || []).map((c) => c.name || c).filter(Boolean),
      });
    });
  }, [isOpen]);

  const loadPreview = useCallback(async (importId, nextFilter = filter, nextPage = page) => {
    const { data } = await api.get(`/clinic/leads/imports/${importId}/preview`, {
      params: { status: nextFilter, page: nextPage, page_size: PAGE_SIZE },
    });
    setSession((prev) => ({ ...(prev || {}), ...data }));
    setRows(data.rows || []);
    setFilteredCount(data.filtered_count || 0);
    setCanAssignOthers(!!data.can_assign_others);
    return data;
  }, [filter, page]);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/clinic/leads/imports', form);
      setSession(data);
      await loadPreview(data.import_id, 'all', 1);
      setStage('map');
    } catch (err) {
      toast.error(importError(err, 'uploadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const applyMapping = async (mapping, sheet) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/clinic/leads/imports/${session.import_id}/mapping`, {
        mapping, sheet: sheet || session.active_sheet,
      });
      setSession(data);
      await loadPreview(session.import_id, 'all', 1);
      setFilter('all'); setPage(1);
    } catch (err) {
      toast.error(importError(err, 'mappingFailed'));
    } finally {
      setBusy(false);
    }
  };

  const patchRows = async (payload) => {
    setBusy(true);
    try {
      await api.patch(`/clinic/leads/imports/${session.import_id}/rows`, payload);
      await loadPreview(session.import_id, filter, page);
    } catch (err) {
      toast.error(importError(err, 'updateFailed'));
    } finally {
      setBusy(false);
    }
  };

  // The confirmation is bound to the exact rows shown: the server rejects it
  // if anything was edited, assigned or excluded since this preview.
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clinic/leads/imports/${session.import_id}/commit`, {
        confirmed_count: session?.summary?.selected_rows ?? 0,
        staging_revision: session?.staging_revision,
        selected_row_ids: session?.selected_row_ids || [],
      });
      setResult(data);
      setStage('result');
      onImported?.();
    } catch (err) {
      const code = err?.response?.data?.code || err?.response?.data?.detail?.code;
      if (code === 'stale_confirmation' && session?.import_id) {
        await loadPreview(session.import_id, filter, page);
      }
      toast.error(importError(err, 'commitFailed'));
    } finally {
      setBusy(false);
    }
  };

  // A run with failed rows stays retryable: only the failed rows are retried
  // and an already-created lead is never created twice.
  const retryFailed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clinic/leads/imports/${session.import_id}/commit`, {
        selected_row_ids: result?.retryable_failed_rows || [],
      });
      setResult(data);
      onImported?.();
    } catch (err) {
      toast.error(importError(err, 'commitFailed'));
    } finally {
      setBusy(false);
    }
  };

  const changeFilter = async (next) => {
    setFilter(next); setPage(1);
    await loadPreview(session.import_id, next, 1);
  };

  const changePage = async (next) => {
    setPage(next);
    await loadPreview(session.import_id, filter, next);
  };

  const totalPages = Math.max(1, Math.ceil(filteredCount / PAGE_SIZE));
  const repName = useMemo(() => {
    const map = {};
    reps.forEach((r) => { map[r.user_id || r.rep_id] = r.name; });
    return map;
  }, [reps]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-2 sm:p-6" data-testid="bulk-import-modal">
      <div className="w-full max-w-6xl max-h-full bg-white rounded-xl shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b px-4 py-3 bg-white rounded-t-xl shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileSpreadsheet className="h-5 w-5 text-teal-700 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-slate-900 truncate" data-testid="bulk-import-title">
                {t('provider.leads.bulkImport.title')}
              </h2>
              <p className="text-[11px] text-slate-500 truncate">{t('provider.leads.bulkImport.subtitle')}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="bulk-import-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="px-4 py-4 space-y-4 overflow-y-auto grow" data-testid="bulk-import-body">
          <ol className="flex flex-wrap gap-2 text-[11px]" data-testid="bulk-import-stepper">
            {['upload', 'map', 'review', 'result'].map((key) => (
              <li
                key={key}
                className={`px-2 py-1 rounded-full border ${
                  stage === key ? 'bg-teal-50 border-teal-300 text-teal-800'
                    : 'bg-slate-50 border-slate-200 text-slate-500'
                }`}
                data-testid={`bulk-import-step-${key}`}
              >
                {t(`provider.leads.bulkImport.steps.${key}`)}
              </li>
            ))}
          </ol>

          {/* Consent / privacy notice — importing never implies consent. */}
          <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="bulk-import-consent-note">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-800">{t('provider.leads.bulkImport.consentNotice')}</p>
          </div>

          {stage === 'upload' && (
            <div className="space-y-3">
              <div className="rounded-xl border-2 border-dashed border-slate-300 px-4 py-10 text-center">
                <Upload className="h-6 w-6 mx-auto text-slate-400" />
                <p className="mt-2 text-sm text-slate-700">{t('provider.leads.bulkImport.uploadPrompt')}</p>
                <p className="text-[11px] text-slate-500 mt-1">{t('provider.leads.bulkImport.uploadLimits')}</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xlsm,.csv"
                  className="hidden"
                  data-testid="bulk-import-file-input"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="bulk-import-choose-file">
                    {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                    {t('provider.leads.bulkImport.chooseFile')}
                  </Button>
                  <Button
                    variant="outline"
                    data-testid="bulk-import-template-btn"
                    onClick={async () => {
                      try {
                        const { data } = await api.get('/clinic/leads/import-template.xlsx', { responseType: 'blob' });
                        downloadBlob(data, 'treatx-lead-import-template-v1.xlsx');
                      } catch (err) {
                        toast.error(importError(err, 'templateFailed'));
                      }
                    }}
                  >
                    <Download className="h-4 w-4 mr-1" />{t('provider.leads.bulkImport.downloadTemplate')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {stage === 'map' && session && (
            <div className="space-y-3" data-testid="bulk-import-mapping">
              {(session.sheet_names || []).length > 1 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-600">{t('provider.leads.bulkImport.worksheet')}</span>
                  <select
                    className="border rounded-md text-sm p-1"
                    value={session.active_sheet || ''}
                    data-testid="bulk-import-sheet-select"
                    onChange={(e) => applyMapping({}, e.target.value)}
                  >
                    {session.sheet_names.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
              <div className="grid gap-2 sm:grid-cols-2">
                {(session.headers || []).map((header) => (
                  <div key={header} className="flex items-center gap-2" data-testid={`map-row-${header}`}>
                    <span className="text-xs text-slate-700 truncate flex-1" title={header}>{header}</span>
                    <select
                      className="border rounded-md text-xs p-1 w-44"
                      value={session.mapping?.[header] || ''}
                      data-testid={`map-select-${header}`}
                      onChange={(e) => applyMapping(
                        { ...(session.mapping || {}), [header]: e.target.value || null },
                        session.active_sheet,
                      )}
                    >
                      <option value="">{t('provider.leads.bulkImport.ignoreColumn')}</option>
                      {(session.canonical_fields || []).map((f) => (
                        <option key={f} value={f}>{t(`provider.leads.bulkImport.fields.${f}`)}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              <SummaryStrip summary={session.summary} t={t} />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setStage('upload')} data-testid="bulk-import-back-upload">
                  {t('common.back', 'Back')}
                </Button>
                <Button onClick={() => setStage('review')} disabled={busy} data-testid="bulk-import-to-review">
                  {t('provider.leads.bulkImport.continueToReview')}
                </Button>
              </div>
            </div>
          )}

          {stage === 'review' && session && (
            <div className="space-y-3" data-testid="bulk-import-review">
              <SummaryStrip summary={session.summary} t={t} />

              <div className="flex flex-wrap items-center gap-2">
                <Filter className="h-3.5 w-3.5 text-slate-400" />
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    onClick={() => changeFilter(f)}
                    className={`text-[11px] px-2 py-1 rounded-full border ${
                      filter === f ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 border-slate-200'
                    }`}
                    data-testid={`bulk-import-filter-${f}`}
                  >
                    {t(`provider.leads.bulkImport.filters.${f}`)}
                  </button>
                ))}
                <div className="flex-1" />
                <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy}
                  onClick={() => patchRows({ exclude_invalid: true })} data-testid="bulk-import-exclude-invalid">
                  {t('provider.leads.bulkImport.excludeInvalid')}
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy}
                  onClick={() => patchRows({ exclude_duplicates: true })} data-testid="bulk-import-exclude-duplicates">
                  {t('provider.leads.bulkImport.excludeDuplicates')}
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy}
                  onClick={() => patchRows({ restore_all: true })} data-testid="bulk-import-restore-all">
                  <RotateCcw className="h-3 w-3 mr-1" />{t('provider.leads.bulkImport.restoreAll')}
                </Button>
              </div>

              {canAssignOthers && Object.keys(session?.summary?.assignment_distribution || {}).length > 0 && (
                <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="assignment-distribution">
                  <span className="text-slate-500">{t('provider.leads.bulkImport.distribution')}</span>
                  {Object.entries(session.summary.assignment_distribution).map(([agent, count]) => (
                    <Badge
                      key={agent}
                      variant="outline"
                      className="font-normal"
                      data-testid={`distribution-${agent}`}
                    >
                      {(agent === '__self__' ? t('provider.leads.bulkImport.assignToMe') : (repName[agent] || agent))}
                      {` · ${count}`}
                    </Badge>
                  ))}
                </div>
              )}

              {canAssignOthers && reps.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap rounded-lg border border-slate-200 px-3 py-2">
                  <Users className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-xs text-slate-600">{t('provider.leads.bulkImport.assignAll')}</span>
                  <select
                    className="border rounded-md text-xs p-1"
                    defaultValue=""
                    data-testid="bulk-import-assign-all"
                    onChange={(e) => patchRows({ assign_all_to: e.target.value })}
                  >
                    <option value="">{t('provider.leads.bulkImport.assignToMe')}</option>
                    {reps.map((r) => (
                      <option key={r.user_id || r.rep_id} value={r.user_id || r.rep_id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* The grid is wider than the modal, so it owns BOTH scroll
                  axes and is height-capped: the horizontal scrollbar then sits
                  inside the viewport instead of 500px below the fold, where
                  the right-hand columns were unreachable. */}
              <div
                className="overflow-auto overscroll-contain border rounded-lg max-h-[40vh]"
                data-testid="bulk-import-grid-scroll"
              >
                <table className="min-w-full text-xs" data-testid="bulk-import-grid">
                  <thead className="bg-slate-50 sticky top-0 z-10 [&_th]:bg-slate-50">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium text-slate-600">#</th>
                      {EDITABLE.map((f) => (
                        <th key={f} className="px-2 py-2 text-left font-medium text-slate-600 whitespace-nowrap">
                          {t(`provider.leads.bulkImport.fields.${f}`)}
                        </th>
                      ))}
                      <th className="px-2 py-2 text-left font-medium text-slate-600">
                        {t('provider.leads.bulkImport.fields.assigned_agent_id')}
                      </th>
                      <th className="px-2 py-2 text-left font-medium text-slate-600">
                        {t('provider.leads.bulkImport.statusColumn')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.row_id}
                        className={ROW_TINT(row)}
                        data-state={row.valid ? (row.excluded ? 'excluded' : 'valid') : 'invalid'}
                        data-testid={`bulk-import-row-${row.row_id}`}>
                        <td className={`px-2 py-1 ${row.valid ? 'text-slate-500' : 'text-rose-800 font-semibold'}`}>
                          {row.source_line}
                        </td>
                        {EDITABLE.map((field) => (
                          <td key={field} className="px-1 py-1">
                            {field === 'treatment_interest' || field === 'patient_country'
                              || field === 'priority' || field === 'source_channel' ? (
                              <select
                                className="border rounded w-36 p-1 text-[11px]"
                                value={row.values?.[field] || ''}
                                data-testid={`cell-${row.row_id}-${field}`}
                                onChange={(e) => patchRows({ rows: [{ row_id: row.row_id, values: { [field]: e.target.value } }] })}
                              >
                                <option value="">—</option>
                                {(field === 'treatment_interest' ? catalog.treatments
                                  : field === 'patient_country' ? catalog.countries
                                  : field === 'priority' ? PRIORITIES : SOURCE_CHANNELS
                                ).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                                {row.values?.[field]
                                  && !(field === 'treatment_interest' ? catalog.treatments
                                    : field === 'patient_country' ? catalog.countries
                                    : field === 'priority' ? PRIORITIES : SOURCE_CHANNELS
                                  ).includes(row.values[field]) && (
                                  <option value={row.values[field]}>{row.values[field]}</option>
                                )}
                              </select>
                            ) : (
                              <Input
                                className="h-7 w-36 text-[11px]"
                                key={`${row.row_id}-${field}-${row.values?.[field] || ''}`}
                                defaultValue={
                                  field === 'patient_name'
                                    ? (row.values?.patient_name || row.normalised?.patient_name || '')
                                    : (row.values?.[field] || '')
                                }
                                data-testid={`cell-${row.row_id}-${field}`}
                                onBlur={(e) => {
                                  if ((row.values?.[field] || '') !== e.target.value) {
                                    patchRows({ rows: [{ row_id: row.row_id, values: { [field]: e.target.value } }] });
                                  }
                                }}
                              />
                            )}
                          </td>
                        ))}
                        <td className="px-1 py-1">
                          {canAssignOthers && reps.length > 0 ? (
                            <select
                              className="border rounded w-32 p-1 text-[11px]"
                              value={row.assigned_agent_id || ''}
                              data-testid={`cell-${row.row_id}-assignee`}
                              onChange={(e) => patchRows({ rows: [{ row_id: row.row_id, assigned_agent_id: e.target.value }] })}
                            >
                              <option value="">{t('provider.leads.bulkImport.assignToMe')}</option>
                              {reps.map((r) => (
                                <option key={r.user_id || r.rep_id} value={r.user_id || r.rep_id}>{r.name}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-[11px] text-slate-500">
                              {repName[row.assigned_agent_id] || t('provider.leads.bulkImport.assignToMe')}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1 space-y-1 min-w-[180px]">
                          <div className="flex flex-wrap items-center gap-1">
                            {row.valid
                              ? <Badge className="bg-teal-50 text-teal-700 text-[10px] border border-teal-200">{t('provider.leads.bulkImport.badges.valid')}</Badge>
                              : (
                                <Badge
                                  className="bg-rose-600 text-white text-[10px] border border-rose-700 gap-1"
                                  data-testid={`invalid-badge-${row.row_id}`}
                                >
                                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                                  {t('provider.leads.bulkImport.badges.invalid')}
                                </Badge>
                              )}
                            {row.duplicate && (
                              <Badge className="bg-amber-50 text-amber-700 text-[10px] border border-amber-200"
                                data-testid={`dup-badge-${row.row_id}`}>
                                {t(`provider.leads.bulkImport.duplicates.${row.duplicate.kind}`)}
                              </Badge>
                            )}
                            {BLOCKING_DUPLICATES.includes(row.duplicate?.kind) ? (
                              <span className="text-[10px] text-amber-700" data-testid={`blocked-${row.row_id}`}>
                                {t('provider.leads.bulkImport.blockedDuplicate')}
                              </span>
                            ) : (
                              <button
                                className={`text-[10px] underline ${row.valid ? 'text-slate-500' : 'text-rose-800'}`}
                                data-testid={`toggle-exclude-${row.row_id}`}
                                onClick={() => patchRows({ rows: [{ row_id: row.row_id, excluded: !row.excluded }] })}
                              >
                                {row.excluded ? t('provider.leads.bulkImport.restoreRow') : t('provider.leads.bulkImport.excludeRow')}
                              </button>
                            )}
                          </div>
                          {row.errors?.length > 0 && <RowErrors errors={row.errors} t={t} />}
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr><td colSpan={EDITABLE.length + 3} className="px-3 py-6 text-center text-slate-400">
                        {t('provider.leads.bulkImport.noRowsForFilter')}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" className="h-7" disabled={page <= 1 || busy}
                    onClick={() => changePage(page - 1)} data-testid="bulk-import-prev-page">
                    <ChevronLeft className="h-3 w-3" />
                  </Button>
                  <span className="text-[11px] text-slate-500" data-testid="bulk-import-page-label">
                    {t('provider.leads.bulkImport.pageOf', { page, total: totalPages })}
                  </span>
                  <Button size="sm" variant="outline" className="h-7" disabled={page >= totalPages || busy}
                    onClick={() => changePage(page + 1)} data-testid="bulk-import-next-page">
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStage('map')} data-testid="bulk-import-back-map">
                    {t('common.back', 'Back')}
                  </Button>
                  <Button
                    onClick={commit}
                    disabled={busy || !(session?.summary?.selected_rows > 0)}
                    data-testid="bulk-import-commit"
                  >
                    {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                    {t('provider.leads.bulkImport.commit', { count: session?.summary?.selected_rows ?? 0 })}
                  </Button>
                </div>
              </div>
              <p className="text-[11px] text-slate-500" data-testid="bulk-import-nothing-created-note">
                {t('provider.leads.bulkImport.nothingCreatedYet')}
              </p>
            </div>
          )}

          {stage === 'result' && result && (
            <div className="space-y-3" data-testid="bulk-import-result">
              <div className="grid grid-cols-3 gap-2">
                {[['created', 'text-teal-700'], ['skipped', 'text-slate-600'], ['failed', 'text-rose-700']].map(([key, tone]) => (
                  <div key={key} className="rounded-lg border border-slate-200 px-3 py-2" data-testid={`result-${key}`}>
                    <div className={`text-lg font-semibold ${tone}`}>{result[key] ?? 0}</div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">
                      {t(`provider.leads.bulkImport.result.${key}`)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto border rounded-lg max-h-80">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium text-slate-600">#</th>
                      <th className="px-2 py-2 text-left font-medium text-slate-600">{t('provider.leads.bulkImport.fields.patient_name')}</th>
                      <th className="px-2 py-2 text-left font-medium text-slate-600">{t('provider.leads.bulkImport.result.outcome')}</th>
                      <th className="px-2 py-2 text-left font-medium text-slate-600">{t('provider.leads.bulkImport.result.reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.rows || []).map((row) => (
                      <tr key={row.row_id} data-testid={`result-row-${row.row_id}`}>
                        <td className="px-2 py-1 text-slate-500">{row.source_line}</td>
                        <td className="px-2 py-1">{row.patient_name}</td>
                        <td className="px-2 py-1">{t(`provider.leads.bulkImport.outcomes.${row.outcome}`)}</td>
                        <td className="px-2 py-1 text-slate-500">
                          {row.reason ? t(`provider.leads.bulkImport.reasons.${row.reason}`, { defaultValue: row.reason }) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                {(result?.retryable_failed_rows || []).length > 0 && (
                  <Button
                    variant="outline"
                    data-testid="bulk-import-retry-failed"
                    disabled={busy}
                    onClick={retryFailed}
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    {t('provider.leads.bulkImport.retryFailed', { count: result.retryable_failed_rows.length })}
                  </Button>
                )}
                <Button
                  variant="outline"
                  data-testid="bulk-import-download-result"
                  onClick={async () => {
                    try {
                      const { data } = await api.get(
                        `/clinic/leads/imports/${session.import_id}/result.csv`, { responseType: 'blob' });
                      downloadBlob(data, `lead-import-${session.import_id}.csv`);
                    } catch (err) {
                      toast.error(importError(err, 'downloadFailed'));
                    }
                  }}
                >
                  <Download className="h-4 w-4 mr-1" />{t('provider.leads.bulkImport.downloadResult')}
                </Button>
                <Button onClick={onClose} data-testid="bulk-import-done">{t('provider.leads.bulkImport.done')}</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkLeadImport;
